import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CsrMeasurementUnit } from '../../../models/csrMeasurementUnit';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { ColumnConfig, LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { LookupTableSearchBoxComponent } from '../../shared/lookup-table-search-box/lookup-table-search-box.component';
import { LookupTableSearchFilter } from '../../../models/lookupTableSearchFilter';
import { LutService } from '../../../services/lut.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-csr-measurement-unit-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule,
    LookupTableSearchBoxComponent
  ],
  templateUrl: './csr-measurement-unit-list.component.html',
  styleUrl: './csr-measurement-unit-list.component.scss'
})
export class CsrMeasurementUnitListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  isSearchVisible = false;
  searchFilter: LookupTableSearchFilter = {};

  csrMeasurementUnits = signal<CsrMeasurementUnit[]>([]);
  allCsrMeasurementUnits: CsrMeasurementUnit[] = [];

  csrMeasurementUnitsColumns: ColumnConfig[] = [
    { key: 'name', header: 'Name', type: 'text', sortable: true, editable: true },
    { key: 'type', header: 'Type', type: 'select', sortable: true, editable: true, selectOptions: [
      { value: 1, label: 'Other' },
      { value: 5, label: 'Emissions' },
      { value: 2, label: 'Energy' },
      { value: 4, label: 'Water' },
      { value: 3, label: 'Waste' },
      { value: 6, label: 'Energy Intensity' },
      { value: 7, label: 'Water Intensity' }
    ] },
    { key: 'converter', header: 'Converter', type: 'text', sortable: true, editable: true }
  ];

  rightItem: CsrMeasurementUnit | undefined = undefined;
  matchedItems: CsrMeasurementUnit[] | undefined = undefined;

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadCsrMeasurementUnits();
  }

  loadCsrMeasurementUnits(): void {
    this.spinnerService.show();
    this.lutService.getCsrMeasurementUnits().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortCsrMeasurementUnits(data);
        this.allCsrMeasurementUnits = sortedData;
        this.csrMeasurementUnits.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading ESG measurement units:', error);
        this.spinnerService.hide();
        this.toast.error(`<strong>Failed to load ESG Measurement Units:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  private sortCsrMeasurementUnits(units: CsrMeasurementUnit[]): CsrMeasurementUnit[] {
    return [...units].sort((a, b) => a.name.localeCompare(b.name));
  }

  onCreateCsrMeasurementUnit(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(csrMeasurementUnit: CsrMeasurementUnit): void {
  }

  onEditCsrMeasurementUnit(csrMeasurementUnit: CsrMeasurementUnit): void {
  }

  onDeleteCsrMeasurementUnit(csrMeasurementUnit: CsrMeasurementUnit): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(csrMeasurementUnit.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalCsrMeasurementUnits = this.csrMeasurementUnits();

          // 1. Optimistically remove from local state immediately
          this.csrMeasurementUnits.update(units => units.filter(unit => unit.id !== csrMeasurementUnit.id));
          this.allCsrMeasurementUnits = this.allCsrMeasurementUnits.filter(unit => unit.id !== csrMeasurementUnit.id);

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteCsrMeasurementUnit(csrMeasurementUnit.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('ESG measurement unit deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting ESG measurement unit:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.csrMeasurementUnits.set(originalCsrMeasurementUnits);
              this.allCsrMeasurementUnits = [...originalCsrMeasurementUnits];
              this.toast.error(`<strong>Failed to delete ESG Measurement Unit:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: CsrMeasurementUnit, newName: string} | {item: CsrMeasurementUnit, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: CsrMeasurementUnit;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      let newValue: any = event.newValue;

      // Convert string to number for converter and type fields
      if (event.columnKey === 'converter') {
        newValue = event.newValue === '' || event.newValue === null ? null : parseFloat(event.newValue);
      } else if (event.columnKey === 'type') {
        newValue = parseInt(event.newValue);
      }

      updatedItem = { ...event.item, [event.columnKey]: newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.csrMeasurementUnits.update(units => {
      const updated = units.map(unit => unit.id === event.item.id ? updatedItem : unit);
      return this.sortCsrMeasurementUnits(updated);
    });
    this.allCsrMeasurementUnits = this.sortCsrMeasurementUnits(
      this.allCsrMeasurementUnits.map(unit => unit.id === event.item.id ? updatedItem : unit)
    );

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateCsrMeasurementUnit(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('ESG measurement unit updated successfully');
        // 3. On success: Update with server response and re-sort to maintain order
        this.csrMeasurementUnits.update(units => {
          const updated = units.map(unit => unit.id === event.item.id ? response : unit);
          return this.sortCsrMeasurementUnits(updated);
        });
        this.allCsrMeasurementUnits = this.sortCsrMeasurementUnits(
          this.allCsrMeasurementUnits.map(unit => unit.id === event.item.id ? response : unit)
        );
      },
      error: (error) => {
        console.error('Error updating ESG measurement unit:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.csrMeasurementUnits.update(units =>
          units.map(unit => unit.id === event.item.id ? originalItem : unit)
        );
        this.allCsrMeasurementUnits = this.allCsrMeasurementUnits.map(unit => unit.id === event.item.id ? originalItem : unit);
        this.toast.error(`<strong>Failed to update ESG Measurement Unit:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();

    // Parse numeric fields
    const typeAsNumber = parseInt(event['type']);
    const converterAsNumber = event['converter'] === '' || event['converter'] === null ? null : parseFloat(event['converter']);

    const newCsrMeasurementUnit: CsrMeasurementUnit = {
      id: tempId,
      name: event['name'],
      type: typeAsNumber,
      converter: converterAsNumber,
      systemReserved: false
    };

    // 1. Optimistically add to local state immediately and sort
    this.csrMeasurementUnits.update(units => this.sortCsrMeasurementUnits([...units, newCsrMeasurementUnit]));
    this.allCsrMeasurementUnits = this.sortCsrMeasurementUnits([...this.allCsrMeasurementUnits, newCsrMeasurementUnit]);

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createCsrMeasurementUnit({
      name: event['name'],
      type: typeAsNumber,
      converter: converterAsNumber,
      systemReserved: false
    }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('ESG measurement unit created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain order
        this.csrMeasurementUnits.update(units => {
          const updated = units.map(unit => unit.id === tempId ? response : unit);
          return this.sortCsrMeasurementUnits(updated);
        });
        this.allCsrMeasurementUnits = this.sortCsrMeasurementUnits(
          this.allCsrMeasurementUnits.map(unit => unit.id === tempId ? response : unit)
        );
      },
      error: (error) => {
        console.error('Error creating ESG measurement unit:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.csrMeasurementUnits.update(units =>
          units.filter(unit => unit.id !== tempId)
        );
        this.allCsrMeasurementUnits = this.allCsrMeasurementUnits.filter(unit => unit.id !== tempId);
        this.toast.error(`<strong>Failed to create ESG Measurement Unit:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onReadyToMatch(event: {rightItem?: CsrMeasurementUnit, matchedItems?: CsrMeasurementUnit[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onCsrMeasurementUnitCorrected(): void {
    // Validate that we have the required data
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    // Prepare the bulk merge request
    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    // Show confirmation dialog
    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        // Store original list for rollback in case of error
        const originalCsrMeasurementUnits = this.csrMeasurementUnits();

        // 1. Optimistically remove merged items from local state
        this.csrMeasurementUnits.update(units =>
          units.filter(unit => !bulkMergeRequest.leftIds.includes(unit.id))
        );
        this.allCsrMeasurementUnits = this.allCsrMeasurementUnits.filter(unit => !bulkMergeRequest.leftIds.includes(unit.id));

        // Clear the selection state
        this.rightItem = undefined;
        this.matchedItems = undefined;

        // 2. Call backend API
        this.spinnerService.show();
        this.lutService.mergeCsrMeasurementUnitsBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('ESG measurement units merged successfully');
            // 3. On success: Clear the lookup table's internal matching state and reload the list
            this.lookupTable.clearMatchingState();
            this.loadCsrMeasurementUnits();
          },
          error: (error) => {
            console.error('Error merging ESG measurement units:', error);
            this.spinnerService.hide();
            // 4. On error: Restore original list
            this.csrMeasurementUnits.set(originalCsrMeasurementUnits);
            this.allCsrMeasurementUnits = [...originalCsrMeasurementUnits];
            // Restore selection state for retry
            this.rightItem = originalCsrMeasurementUnits.find(unit => unit.id === bulkMergeRequest.rightId);
            this.matchedItems = originalCsrMeasurementUnits.filter(unit => bulkMergeRequest.leftIds.includes(unit.id));
            this.toast.error(`<strong>Failed to merge ESG Measurement Units:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }

  onSearchFilterChange(searchFilter: LookupTableSearchFilter): void {
    this.searchFilter = searchFilter;
    if (searchFilter.name) {
      this.csrMeasurementUnits.set(
        this.allCsrMeasurementUnits.filter(unit =>
          unit.name.toLowerCase().includes(searchFilter.name?.toLowerCase() ?? '')
        )
      );
    } else {
      this.csrMeasurementUnits.set(this.allCsrMeasurementUnits);
    }
  }

  getAppliedFiltersCount(): number {
    return Object.values(this.searchFilter).filter(value => value !== undefined && value !== null && value !== '').length;
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }
}
